import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { notification } from 'antd';

import {
  ALL_SENSORS,
  NozomiVantageAssetCounts,
  RefreshStatus,
} from '@totem/components/administration/reconciliation/nozomi/vantage/types';
import VantageAssetCountsContext from '@totem/components/administration/reconciliation/nozomi/vantage/VantageAssetCountsContext';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Job } from '@totem/types/job';
import {
  JOBS_ENDPOINT,
  NOZOMI_VANTAGE_ASSET_COUNTS_ENDPOINT,
} from '@totem/utilities/endpoints';

const POLL_INTERVAL_MS = 3000;

// device-sync-service stops a count before the Cloud Function's 9-minute timeout. A job still unfinished after
// this long was cut off without updating the job, so stop waiting for it.
const STALE_JOB_MS = 10 * 60 * 1000;

type Props = {
  children: ReactNode;
};

const VantageAssetCountsContainer = ({ children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NozomiVantageAssetCounts | null>(null);
  const [refreshing, setRefreshing] = useState<Record<string, RefreshStatus>>(
    {},
  );
  const [reloadCount, setReloadCount] = useState(0);
  const pollTimeouts = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  // When each refresh (keyed like refreshing) was started.
  const refreshStartedAt = useRef<Record<string, number>>({});

  useEffect(
    () => () => {
      Object.values(pollTimeouts.current).forEach(clearTimeout);
    },
    [],
  );

  useEffect(() => {
    setLoading(true);

    fetch(NOZOMI_VANTAGE_ASSET_COUNTS_ENDPOINT, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((counts: NozomiVantageAssetCounts) => setData(counts))
      .catch(() => {
        notification.error({
          message: 'Failed to load Vantage asset counts.',
        });
      })
      .finally(() => setLoading(false));
  }, [getApiToken, reloadCount]);

  const onReload = useCallback(() => {
    setReloadCount((count) => count + 1);
  }, []);

  const finishRefresh = useCallback((key: string) => {
    delete pollTimeouts.current[key];

    delete refreshStartedAt.current[key];

    setRefreshing((current) => {
      const remaining = { ...current };
      delete remaining[key];
      return remaining;
    });
  }, []);

  const pollJob = useCallback(
    (key: string, jobId: string) => {
      fetch(`${JOBS_ENDPOINT}/${jobId}`, {
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      })
        .then((res) => res.json())
        .then((job: Job<{ success: boolean; error?: string }>) => {
          if (job.status === 'completed' || job.status === 'failed') {
            finishRefresh(key);

            setReloadCount((count) => count + 1);

            if (job.status === 'failed' || job.result?.success === false) {
              notification.warning({
                message:
                  key === ALL_SENSORS
                    ? 'Some sensors could not be counted.'
                    : 'The sensor could not be counted.',
                description: job.error || job.result?.error || undefined,
              });
            }

            return;
          }

          if (
            Date.now() - (refreshStartedAt.current[key] ?? Date.now()) >
            STALE_JOB_MS
          ) {
            finishRefresh(key);

            setReloadCount((count) => count + 1);

            notification.error({
              message: 'The count did not finish.',
              description:
                'It has not reported back in 10 minutes and was probably stopped by the server. Counts it finished are shown; recount to try the rest.',
            });

            return;
          }

          setRefreshing((current) => ({
            ...current,
            [key]: job.status as RefreshStatus,
          }));

          pollTimeouts.current[key] = setTimeout(
            () => pollJob(key, jobId),
            POLL_INTERVAL_MS,
          );
        })
        .catch(() => {
          finishRefresh(key);

          notification.error({ message: 'Failed to check the count status.' });
        });
    },
    [getApiToken, finishRefresh],
  );

  const onRefreshCounts = useCallback(
    (siteId?: string) => {
      const key = siteId ?? ALL_SENSORS;
      refreshStartedAt.current[key] = Date.now();

      setRefreshing((current) => ({ ...current, [key]: 'pending' }));

      const query = siteId ? `?siteId=${encodeURIComponent(siteId)}` : '';
      fetch(`${NOZOMI_VANTAGE_ASSET_COUNTS_ENDPOINT}/refresh${query}`, {
        method: 'GET',
        headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error(`HTTP ${res.status}`);
          }

          return res.json();
        })
        .then((response: { jobId: string }) => pollJob(key, response.jobId))
        .catch(() => {
          finishRefresh(key);

          notification.error({ message: 'Failed to start the count.' });
        });
    },
    [getApiToken, pollJob, finishRefresh],
  );

  return (
    <VantageAssetCountsContext.Provider
      value={{ loading, data, refreshing, onReload, onRefreshCounts }}
    >
      {children}
    </VantageAssetCountsContext.Provider>
  );
};

export default VantageAssetCountsContainer;
